import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Actions as ApiCallActions } from '../../../../redux/apiCall/reducers'
import { Actions as AuthActions } from '../../../../redux/auth/reducers'
import general from '../../../../utils/general'
import store from 'store'
import FileUpload from '../../../../components/file-upload'
import Input from '../../../../components/input'
import { useNavigate } from 'react-router-dom'
import './RegisterProfileComplete.scss'
import EventBus from '../../../../infrastructure/event-bus/event-bus'
import { ValidationPatterns, ValidationMessages } from '../../../../utils/validation'
import useI18n from '../../../../i18n/useI18n'

const RegisterProfileComplete = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { t } = useI18n()
    const currentUserInfo = useSelector((state) => state.auth?.userInfo || {})

    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [studentId, setStudentId] = useState(null)
    const [validationErrors, setValidationErrors] = useState({})

    // Form State
    const [formData, setFormData] = useState({
        // Identity
        dateOfBirth: '',
        nationality: '',
        city: '',
        country: '',
        // Contact
        address: '',
        postalCode: '',
        // Education
        educationLevel: '',
        gpa: '',
        languageEnglish: '',
        languageTurkish: '',
        // Emergency
        emergencyContactName: '',
        emergencyContactPhone: '',
        emergencyContactRelation: '',
        // Documents
        passportFile: '',
        highschoolTranscript: '',
        highschoolDiploma: '',

        // Other
        hobbies: '',
        kvkkStatus: false
    })

    useEffect(() => {
        fetchStudentProfile()
    }, [])

    const fetchStudentProfile = () => {
        setLoading(true)
        dispatch(
            ApiCallActions.Get({
                url: 'students/me',
                showLoading: false,
                onSuccess: ({ data }) => {
                    const student = data?.data || data
                    if (student?.id) {
                        setStudentId(student.id)
                        // Map existing data if any
                        setFormData(prev => ({
                            ...prev,
                            // Map logic here similar to StudentForm but cleaner
                            dateOfBirth: student.dateOfBirth || '',
                            nationality: student.nationality || '',
                            city: student.city || '',
                            country: student.country || '',
                            address: student.address || '',
                            postalCode: student.postalCode || '',
                            educationLevel: student.educationLevel || '',
                            gpa: student.gpa || '',
                            languageEnglish: student.languageProficiency?.english || '',
                            languageTurkish: student.languageProficiency?.turkish || '',
                            emergencyContactName: student.emergencyContactName || '',
                            emergencyContactPhone: student.emergencyContactPhone || '',
                            emergencyContactRelation: student.emergencyContactRelation || '',
                            passportFile: student.passportFile || '',
                            highschoolTranscript: student.highschoolTranscript || '',
                            highschoolDiploma: student.highschoolDiploma || '',
                            otherMotivation: student.otherFiles?.motivationLetter || '',
                            hobbies: student.additionalInfo?.hobbies || '',
                            kvkkStatus: student.kvkkStatus || false
                        }))
                    }
                    setLoading(false)
                },
                onError: () => {
                    general.notificationError('Profil bilgileri yüklenemedi.')
                    setLoading(false)
                }
            })
        )
    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))
    }

    const handleValidationChange = (name, isValid, errorMessage) => {
        setValidationErrors(prev => ({
            ...prev,
            [name]: isValid ? '' : errorMessage
        }))
    }

    const handleUploadSuccess = (field, url) => {
        setFormData(prev => ({ ...prev, [field]: url }))
    }

    const buildPayload = () => {
        // Construct payload matching the API expectation
        const payload = {
            dateOfBirth: formData.dateOfBirth,
            nationality: formData.nationality,
            city: formData.city,
            country: formData.country,
            address: formData.address,
            postalCode: formData.postalCode,
            educationLevel: formData.educationLevel,
            gpa: formData.gpa ? parseFloat(formData.gpa) : null,
            emergencyContactName: formData.emergencyContactName,
            emergencyContactPhone: formData.emergencyContactPhone,
            emergencyContactRelation: formData.emergencyContactRelation,
            passportFile: formData.passportFile,
            highschoolTranscript: formData.highschoolTranscript,
            highschoolDiploma: formData.highschoolDiploma,
            kvkkStatus: formData.kvkkStatus,
            languageProficiency: {
                english: formData.languageEnglish,
                turkish: formData.languageTurkish
            },
            otherFiles: {
                motivationLetter: formData.otherMotivation
            },
            additionalInfo: {
                hobbies: formData.hobbies
            }
        }

        // Clean undefined/empty string values if component doesn't handle them
        // (Assuming backend handles nulls gracefully or we need to strip them)
        return payload
    }

    const handleSubmit = () => {
        // Strict validation for required fields
        const requiredFields = [
            'dateOfBirth',
            'nationality',
            'city',
            'country',
            'passportFile',
            'highschoolTranscript',
            'highschoolDiploma'
        ];

        const missingFields = requiredFields.filter(field => !formData[field]);

        if (missingFields.length > 0) {
            general.notificationError('Lütfen zorunlu alanları doldurunuz.');
            return;
        }

        const hasValidationErrors = Object.values(validationErrors).some(err => err !== '');
        if (hasValidationErrors) {
            general.notificationError('Lütfen formdaki hatalı alanları düzeltiniz.');
            return;
        }

        if (!formData.kvkkStatus) {
            general.notificationError(ValidationMessages.REQUIRED);
            return;
        }

        setSubmitting(true)
        const payload = buildPayload()

        dispatch(
            ApiCallActions.Put({
                url: 'students/me',
                data: payload,
                showLoading: true,
                onSuccess: ({ data }) => {
                    const savedData = data?.data || data

                    // Update Redux & Local Storage like StudentForm does
                    const updatedUserInfo = { ...currentUserInfo, student: savedData }
                    store.set('studentProfile', savedData)
                    store.set('userInfo', updatedUserInfo)
                    dispatch(AuthActions.setAuthState({ userInfo: updatedUserInfo }))

                    general.messageSuccess(t('REGISTER_PROFILE_SUCCESS'))

                    setTimeout(() => {
                        navigate('/dashboard')
                    }, 1500)
                },
                onError: ({ errorMessage }) => {
                    general.notificationError(errorMessage || t('REGISTER_PROFILE_ERROR'))
                    setSubmitting(false)
                }
            })
        )
    }

    const renderRequired = (label) => (
        <span>{label} <span className="text-danger">{t('REGISTER_PROFILE_REQUIRED_MARK')}</span></span>
    )

    if (loading) return <div className="text-center p-5">{t('COMMON_LOADING')}</div>

    if (!studentId) return <div className="text-center p-5 text-danger">{t('STUDENT_PROFILE_NOT_FOUND')}</div>

    return (
        <div className="register-profile-complete">
            <div className="profile-header">
                <h4 style={{ marginTop: 0 }}>{t('REGISTER_PROFILE_WELCOME', { name: currentUserInfo.firstName })}</h4>
                <p>{t('REGISTER_PROFILE_SUBTITLE')}</p>
            </div>

            {/* Section 1: Identity & Location */}
            <div className="form-section">
                <div className="section-title">
                    <i className="zmdi zmdi-account-box"></i>
                    <h5>{t('REGISTER_PROFILE_SECTION_IDENTITY')}</h5>
                </div>
                <div className="form-grid">
                    <div className="form-group">
                        <label>{renderRequired(t('REGISTER_PROFILE_FIELD_DATE_OF_BIRTH'))}</label>
                        <Input
                            type="date"
                            name="dateOfBirth"
                            value={formData.dateOfBirth}
                            onChange={handleChange}
                            required
                            errorMessage={ValidationMessages.REQUIRED}
                            onValidationChange={handleValidationChange}
                        />
                    </div>
                    <div className="form-group">
                        <label>{renderRequired(t('REGISTER_PROFILE_FIELD_NATIONALITY'))}</label>
                        <Input
                            type="text"
                            name="nationality"
                            value={formData.nationality}
                            onChange={handleChange}
                            placeholder={t('REGISTER_PROFILE_FIELD_NATIONALITY_PLACEHOLDER')}
                            required
                            errorMessage={ValidationMessages.REQUIRED}
                            onValidationChange={handleValidationChange}
                        />
                    </div>
                    <div className="form-group">
                        <label>{renderRequired(t('REGISTER_PROFILE_FIELD_CITY'))}</label>
                        <Input
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            placeholder={t('REGISTER_PROFILE_FIELD_CITY_PLACEHOLDER')}
                            required
                            errorMessage={ValidationMessages.REQUIRED}
                            onValidationChange={handleValidationChange}
                        />
                    </div>
                    <div className="form-group">
                        <label>{renderRequired(t('REGISTER_PROFILE_FIELD_COUNTRY'))}</label>
                        <Input
                            type="text"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            placeholder={t('REGISTER_PROFILE_FIELD_COUNTRY_PLACEHOLDER')}
                            required
                            errorMessage={ValidationMessages.REQUIRED}
                            onValidationChange={handleValidationChange}
                        />
                    </div>
                </div>
            </div>





            {/* Section 5: Documents */}
            <div className="form-section">
                <div className="section-title">
                    <i className="zmdi zmdi-file-text"></i>
                    <h5>{t('REGISTER_PROFILE_SECTION_DOCUMENTS')}</h5>
                </div>
                <div className="form-grid">
                    <div className="form-group">
                        <FileUpload
                            label={renderRequired(t('REGISTER_PROFILE_FIELD_PASSPORT'))}
                            uploadUrl={`files/students/${studentId}/passport`}
                            currentFileUrl={formData.passportFile}
                            onUploadSuccess={(d) => handleUploadSuccess('passportFile', d.url)}
                            onRemove={() => handleUploadSuccess('passportFile', '')}
                        />
                    </div>
                    <div className="form-group">
                        <FileUpload
                            label={renderRequired(t('REGISTER_PROFILE_FIELD_HIGHSCHOOL_TRANSCRIPT'))}
                            uploadUrl={`files/students/${studentId}/transcript`}
                            currentFileUrl={formData.highschoolTranscript}
                            onUploadSuccess={(d) => handleUploadSuccess('highschoolTranscript', d.url)}
                            onRemove={() => handleUploadSuccess('highschoolTranscript', '')}
                        />
                    </div>
                    <div className="form-group">
                        <FileUpload
                            label={renderRequired(t('REGISTER_PROFILE_FIELD_HIGHSCHOOL_DIPLOMA'))}
                            uploadUrl={`files/students/${studentId}/diploma`}
                            currentFileUrl={formData.highschoolDiploma}
                            onUploadSuccess={(d) => handleUploadSuccess('highschoolDiploma', d.url)}
                            onRemove={() => handleUploadSuccess('highschoolDiploma', '')}
                        />
                    </div>

                </div>
            </div>

            {/* Action Area */}
            <div className="action-area">
                {/* <div className="form-check mb-3">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id="kvkkCheck"
                        name="kvkkStatus"
                        checked={formData.kvkkStatus}
                        onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="kvkkCheck">
                        {t('AUTH_FORM_KVKK_TEXT')}
                    </label>
                </div> */}

                <button
                    className="btn-complete"
                    onClick={handleSubmit}
                    disabled={submitting || !formData.kvkkStatus}
                >
                    {submitting ? t('REGISTER_PROFILE_BUTTON_COMPLETING') : t('REGISTER_PROFILE_BUTTON_COMPLETE')}
                </button>
            </div>
        </div >
    )
}

export default RegisterProfileComplete
